
import { PythonModule } from './types';

// Module 1: OOP Basics
export const PYTHON_OOP_BASICS_MODULE: PythonModule = {
  id: "python-intermediate-1",
  title: "Object-Oriented Programming Basics",
  description: "Learn the fundamentals of OOP in Python: classes, objects, attributes, and methods.",
  content: `
# Object-Oriented Programming Basics

## Introduction to OOP
Object-Oriented Programming (OOP) is a programming paradigm that organizes code around objects and classes. It helps create more organized, reusable, and maintainable code.

## Classes and Objects
A class is a blueprint for creating objects. An object is an instance of a class.

### Creating Classes
\`\`\`python
class Dog:
    # Class attribute (shared by all instances)
    species = "Canis familiaris"
    
    # Constructor method
    def __init__(self, name, age, breed):
        self.name = name        # Instance attribute
        self.age = age          # Instance attribute
        self.breed = breed      # Instance attribute
    
    # Instance method
    def bark(self):
        return f"{self.name} says Woof!"
    
    def get_info(self):
        return f"{self.name} is {self.age} years old and is a {self.breed}"

# Creating objects (instances)
dog1 = Dog("Buddy", 3, "Golden Retriever")
dog2 = Dog("Luna", 2, "Husky")

print(dog1.bark())      # Buddy says Woof!
print(dog2.get_info())  # Luna is 2 years old and is a Husky
\`\`\`

## Instance vs Class Attributes
- **Instance attributes**: Unique to each object
- **Class attributes**: Shared by all instances of the class

\`\`\`python
class Car:
    # Class attribute
    wheels = 4
    
    def __init__(self, make, model, year):
        # Instance attributes
        self.make = make
        self.model = model
        self.year = year

car1 = Car("Toyota", "Camry", 2020)
car2 = Car("Honda", "Civic", 2021)

print(car1.wheels)  # 4 (from class)
print(car2.wheels)  # 4 (from class)
print(car1.make)    # Toyota (instance specific)
\`\`\`

## Methods
Methods are functions defined inside a class that operate on objects.

### Types of Methods
\`\`\`python
class BankAccount:
    def __init__(self, account_number, balance=0):
        self.account_number = account_number
        self.balance = balance
    
    # Instance method
    def deposit(self, amount):
        if amount > 0:
            self.balance += amount
            return f"Deposited \\$\\{amount}. New balance: \\$\\{self.balance}"
        return "Invalid deposit amount"
    
    def withdraw(self, amount):
        if 0 < amount <= self.balance:
            self.balance -= amount
            return f"Withdrew \\$\\{amount}. New balance: \\$\\{self.balance}"
        return "Insufficient funds or invalid amount"
    
    def get_balance(self):
        return f"Current balance: \\$\\{self.balance}"

# Using the class
account = BankAccount("12345", 1000)
print(account.deposit(500))     # Deposited $500. New balance: $1500
print(account.withdraw(200))    # Withdrew $200. New balance: $1300
print(account.get_balance())    # Current balance: $1300
\`\`\`

## The self Parameter
- self refers to the instance of the class
- It must be the first parameter in instance methods
- It is used to access instance attributes and methods

## Encapsulation
Encapsulation is about bundling data and methods together and controlling access to them.

\`\`\`python
class Student:
    def __init__(self, name, student_id):
        self.name = name
        self._student_id = student_id  # Protected attribute (convention)
        self.__grade = None            # Private attribute (name mangling)
    
    def set_grade(self, grade):
        if 0 <= grade <= 100:
            self.__grade = grade
        else:
            print("Invalid grade. Must be between 0 and 100.")
    
    def get_grade(self):
        return self.__grade if self.__grade is not None else "No grade assigned"
    
    def get_student_info(self):
        return f"Student: {self.name}, ID: {self._student_id}, Grade: {self.get_grade()}"

student = Student("Alice", "S12345")
student.set_grade(85)
print(student.get_student_info())
# print(student.__grade)  # This would cause an AttributeError
\`\`\`
  `,
  duration: "70 minutes",
  difficulty: "intermediate",
  order: 1,
  tags: ["python", "oop", "classes", "objects", "encapsulation"],
  objectives: [
    "Understand classes and objects in Python",
    "Create classes with attributes and methods",
    "Use constructors to initialize objects",
    "Distinguish between instance and class attributes",
    "Apply encapsulation principles"
  ],
  pythonPractice: {
    task: "Create a Library Management System using OOP principles",
    initialCode: `# Library Management System
class Book:
    def __init__(self, title, author, isbn):
        # Initialize book attributes
        pass
    
    def get_info(self):
        # Return formatted book information
        pass

class Library:
    def __init__(self, name):
        # Initialize library with name and empty book collection
        pass
    
    def add_book(self, book):
        # Add a book to the library
        pass
    
    def find_book(self, title):
        # Find a book by title
        pass
    
    def list_books(self):
        # List all books in the library
        pass
    
    def get_total_books(self):
        # Return total number of books
        pass

# Test your implementation
# library = Library("City Library")
# book1 = Book("1984", "George Orwell", "978-0451524935")
# book2 = Book("To Kill a Mockingbird", "Harper Lee", "978-0061120084")
# library.add_book(book1)
# library.add_book(book2)
# print(library.get_total_books())
# library.list_books()
`,
    expectedCode: `# Library Management System
class Book:
    def __init__(self, title, author, isbn):
        self.title = title
        self.author = author
        self.isbn = isbn
    
    def get_info(self):
        return f"'{self.title}' by {self.author} (ISBN: {self.isbn})"

class Library:
    def __init__(self, name):
        self.name = name
        self.books = []
    
    def add_book(self, book):
        self.books.append(book)
        print(f"Added: {book.get_info()}")
    
    def find_book(self, title):
        for book in self.books:
            if book.title.lower() == title.lower():
                return book
        return None
    
    def list_books(self):
        print(f"Books in {self.name}:")
        for i, book in enumerate(self.books, 1):
            print(f"{i}. {book.get_info()}")
    
    def get_total_books(self):
        return len(self.books)

# Test the implementation
library = Library("City Library")
book1 = Book("1984", "George Orwell", "978-0451524935")
book2 = Book("To Kill a Mockingbird", "Harper Lee", "978-0061120084")

library.add_book(book1)
library.add_book(book2)
print(f"Total books: {library.get_total_books()}")
library.list_books()

# Find a book
found_book = library.find_book("1984")
if found_book:
    print(f"Found: {found_book.get_info()}")
`,
    expectedOutput: {
      type: 'console',
      content: [
        "Added: '1984' by George Orwell (ISBN: 978-0451524935)",
        "Added: 'To Kill a Mockingbird' by Harper Lee (ISBN: 978-0061120084)",
        "Total books: 2",
        "Books in City Library:",
        "1. '1984' by George Orwell (ISBN: 978-0451524935)",
        "2. 'To Kill a Mockingbird' by Harper Lee (ISBN: 978-0061120084)",
        "Found: '1984' by George Orwell (ISBN: 978-0451524935)"
      ]
    },
    hints: [
      "Use self to reference instance attributes",
      "Store books in a list within the Library class",
      "Use enumerate() to number the books when listing",
      "Remember to call methods on objects with dot notation"
    ]
  },
  quiz: {
    questions: [
      {
        id: "py-oop-1",
        question: "What is the purpose of the __init__ method in a Python class?",
        options: ["To delete an object", "To initialize object attributes", "To define class methods", "To create inheritance"],
        correctAnswer: 1
      },
      {
        id: "py-oop-2",
        question: "What does 'self' represent in a Python class method?",
        options: ["The class itself", "A global variable", "The instance of the class", "The parent class"],
        correctAnswer: 2
      },
      {
        id: "py-oop-3",
        question: "Which naming convention indicates a private attribute in Python?",
        options: ["_attribute", "__attribute", "private_attribute", "attribute_"],
        correctAnswer: 1
      }
    ]
  }
};

// Module 2: Advanced OOP
export const PYTHON_ADVANCED_OOP_MODULE: PythonModule = {
  id: "python-intermediate-2",
  title: "Advanced Object-Oriented Programming",
  description: "Master inheritance, polymorphism, and advanced OOP concepts in Python.",
  content: `
# Advanced Object-Oriented Programming

## Inheritance
Inheritance allows a class to inherit attributes and methods from another class.

### Basic Inheritance
\`\`\`python
# Parent class (Base class)
class Animal:
    def __init__(self, name, species):
        self.name = name
        self.species = species
    
    def make_sound(self):
        return f"{self.name} makes a sound"
    
    def info(self):
        return f"{self.name} is a {self.species}"

# Child class (Derived class)
class Dog(Animal):
    def __init__(self, name, breed):
        super().__init__(name, "Dog")  # Call parent constructor
        self.breed = breed
    
    def make_sound(self):  # Method overriding
        return f"{self.name} barks: Woof!"
    
    def fetch(self):  # New method specific to Dog
        return f"{self.name} fetches the ball"

class Cat(Animal):
    def __init__(self, name, indoor=True):
        super().__init__(name, "Cat")
        self.indoor = indoor
    
    def make_sound(self):
        return f"{self.name} meows: Meow!"
    
    def climb(self):
        return f"{self.name} climbs the tree"

# Using inheritance
dog = Dog("Buddy", "Golden Retriever")
cat = Cat("Whiskers", True);

print(dog.info())        # Inherited method
print(dog.make_sound())  # Overridden method
print(dog.fetch())       # Dog-specific method

print(cat.make_sound())  # Overridden method
print(cat.climb())       # Cat-specific method
\`\`\`

## Polymorphism
Polymorphism allows objects of different classes to be treated as objects of a common base class.

\`\`\`python
class Shape:
    def area(self):
        pass
    
    def perimeter(self):
        pass

class Rectangle(Shape):
    def __init__(self, width, height):
        self.width = width
        self.height = height
    
    def area(self):
        return self.width * self.height
    
    def perimeter(self):
        return 2 * (self.width + self.height)

class Circle(Shape):
    def __init__(self, radius):
        self.radius = radius
    
    def area(self):
        return 3.14159 * self.radius ** 2
    
    def perimeter(self):
        return 2 * 3.14159 * self.radius

# Polymorphism in action
shapes = [
    Rectangle(5, 3),
    Circle(4),
    Rectangle(2, 8)
]

for shape in shapes:
    print(f"Area: {shape.area():.2f}")
    print(f"Perimeter: {shape.perimeter():.2f}")
    print("---")
\`\`\`

## Abstract Base Classes
Abstract classes define a common interface for subclasses.

\`\`\`python
from abc import ABC, abstractmethod

class Vehicle(ABC):
    def __init__(self, make, model):
        self.make = make
        self.model = model
    
    @abstractmethod
    def start_engine(self):
        pass
    
    @abstractmethod
    def stop_engine(self):
        pass
    
    def get_info(self):
        return f"{self.make} {self.model}"

class Car(Vehicle):
    def start_engine(self):
        return f"{self.get_info()} engine started with key"
    
    def stop_engine(self):
        return f"{self.get_info()} engine stopped"

class Motorcycle(Vehicle):
    def start_engine(self):
        return f"{self.get_info()} engine started with kick"
    
    def stop_engine(self):
        return f"{self.get_info()} engine stopped"

# Cannot instantiate abstract class
# vehicle = Vehicle("Generic", "Vehicle")  # This would raise an error

car = Car("Toyota", "Camry")
motorcycle = Motorcycle("Harley", "Davidson");

print(car.start_engine())
print(motorcycle.start_engine())
\`\`\`

## Multiple Inheritance
Python supports multiple inheritance where a class can inherit from multiple parent classes.

\`\`\`python
class Flyable:
    def fly(self):
        return "Flying through the air"

class Swimmable:
    def swim(self):
        return "Swimming in water"

class Duck(Animal, Flyable, Swimmable):
    def __init__(self, name):
        Animal.__init__(self, name, "Duck")
    
    def make_sound(self):
        return f"{self.name} quacks: Quack!"

# Duck inherits from all parent classes
duck = Duck("Donald")
print(duck.info())       # From Animal
print(duck.make_sound()) # Overridden method
print(duck.fly())        # From Flyable
print(duck.swim())       # From Swimmable
\`\`\`

## Class Methods and Static Methods
\`\`\`python
class MathUtils:
    pi = 3.14159
    
    @classmethod
    def circle_area(cls, radius):
        return cls.pi * radius ** 2
    
    @staticmethod
    def add_numbers(a, b):
        return a + b
    
    @staticmethod
    def is_even(number):
        return number % 2 == 0

# Using class and static methods
area = MathUtils.circle_area(5)
sum_result = MathUtils.add_numbers(10, 20)
is_even = MathUtils.is_even(4)

print(f"Circle area: {area}")
print(f"Sum: {sum_result}")
print(f"Is 4 even: {is_even}")
\`\`\`
  `,
  duration: "80 minutes",
  difficulty: "intermediate",
  order: 2,
  tags: ["python", "inheritance", "polymorphism", "abstract", "multiple-inheritance"],
  objectives: [
    "Implement inheritance in Python classes",
    "Override methods in child classes",
    "Use polymorphism for flexible code design",
    "Create abstract base classes",
    "Apply multiple inheritance effectively",
    "Use class methods and static methods"
  ],
  pythonPractice: {
    task: "Create an Employee Management System with inheritance and polymorphism",
    initialCode: `from abc import ABC, abstractmethod

# Base Employee class
class Employee(ABC):
    def __init__(self, name, employee_id, salary):
        # Initialize common employee attributes
        pass
    
    @abstractmethod
    def calculate_bonus(self):
        # Abstract method for calculating bonus
        pass
    
    def get_info(self):
        # Return basic employee information
        pass

class Developer(Employee):
    def __init__(self, name, employee_id, salary, programming_languages):
        # Initialize developer with additional attributes
        pass
    
    def calculate_bonus(self):
        # Developers get 15% bonus
        pass
    
    def code_review(self):
        # Developer-specific method
        pass

class Manager(Employee):
    def __init__(self, name, employee_id, salary, team_size):
        # Initialize manager with team size
        pass
    
    def calculate_bonus(self):
        # Managers get 20% bonus + $1000 per team member
        pass
    
    def conduct_meeting(self):
        # Manager-specific method
        pass

class Company:
    def __init__(self, name):
        self.name = name
        self.employees = []
    
    def add_employee(self, employee):
        # Add employee to company
        pass
    
    def calculate_total_bonuses(self):
        # Calculate total bonuses for all employees
        pass

# Test the system
# my_company = Company("Tech Corp")
# my_dev = Developer("Alice", "D001", 80000, ["Python", "JavaScript"])
# my_manager = Manager("Bob", "M001", 100000, 5)
# my_company.add_employee(my_dev)
# my_company.add_employee(my_manager)
# print(f"Total bonuses: $\\{my_company.calculate_total_bonuses()}")
`,
    expectedCode: `from abc import ABC, abstractmethod

# Base Employee class
class Employee(ABC):
    def __init__(self, name, employee_id, salary):
        self.name = name
        self.employee_id = employee_id
        self.salary = salary
    
    @abstractmethod
    def calculate_bonus(self):
        pass
    
    def get_info(self):
        return f"{self.name} (ID: {self.employee_id}) - Salary: \\$\\{self.salary}"

class Developer(Employee):
    def __init__(self, name, employee_id, salary, programming_languages):
        super().__init__(name, employee_id, salary)
        self.programming_languages = programming_languages
    
    def calculate_bonus(self):
        # Developers get 15% bonus
        return self.salary * 0.15
    
    def code_review(self):
        return f"{self.name} is reviewing code in {', '.join(self.programming_languages)}"

class Manager(Employee):
    def __init__(self, name, employee_id, salary, team_size):
        super().__init__(name, employee_id, salary)
        self.team_size = team_size
    
    def calculate_bonus(self):
        # Managers get 20% bonus + $1000 per team member
        return ( self.salary * 0.20) + (self.team_size * 1000)
    
    def conduct_meeting(self):
        return f"{self.name} is conducting a meeting with {self.team_size} team members"

class Company:
    def __init__(self, name):
        self.name = name
        self.employees = []
    
    def add_employee(self, employee):
        self.employees.append(employee)
        print(f"Added employee: {employee.get_info()}")
    
    def calculate_total_bonuses(self):
        total = sum(emp.calculate_bonus() for emp in self.employees)
        return total

# Test the system
my_company = Company("Tech Corp")
my_dev = Developer("Alice", "D001", 80000, ["Python", "JavaScript"])
my_manager = Manager("Bob", "M001", 100000, 5)

my_company.add_employee(my_dev)
my_company.add_employee(my_manager)

print(f"Developer bonus: \\$\\{my_dev.calculate_bonus()}")
print(f"Manager bonus: \\$\\{my_manager.calculate_bonus()}")
print(f"Total bonuses: \\$\\{my_company.calculate_total_bonuses()}")

print(my_dev.code_review())
print(my_manager.conduct_meeting())
`,
    expectedOutput: {
      type: 'console',
      content: [
        "Added employee: Alice (ID: D001) - Salary: $80000",
        "Added employee: Bob (ID: M001) - Salary: $100000",
        "Developer bonus: $12000.0",
        "Manager bonus: $25000.0",
        "Total bonuses: $37000.0",
        "Alice is reviewing code in Python, JavaScript",
        "Bob is conducting a meeting with 5 team members"
      ]
    },
    hints: [
      "Use super().__init__() to call parent constructor",
      "Implement abstract methods in child classes",
      "Use list comprehension with sum() for total calculations",
      "Store employees in a list and iterate through them"
    ]
  },
  quiz: {
    questions: [
      {
        id: "py-oop-adv-1",
        question: "What is the purpose of the super() function in Python inheritance?",
        options: ["To create a new class", "To call methods from the parent class", "To delete a class", "To check inheritance"],
        correctAnswer: 1
      },
      {
        id: "py-oop-adv-2",
        question: "What is polymorphism in OOP?",
        options: ["Having multiple constructors", "The ability to have multiple forms", "Creating multiple objects", "Inheriting from multiple classes"],
        correctAnswer: 1
      },
      {
        id: "py-oop-adv-3",
        question: "What decorator is used to create an abstract method in Python?",
        options: ["@abstract", "@abstractmethod", "@virtual", "@override"],
        correctAnswer: 1
      }
    ]
  }
};

// Module 3: File Handling
export const PYTHON_FILE_HANDLING_MODULE: PythonModule = {
  id: "python-intermediate-3",
  title: "File Handling and Data Persistence",
  description: "Learn to read, write, and manipulate files in Python for data persistence and processing.",
  content: `
# File Handling and Data Persistence

## Introduction to File Operations
File handling is essential for reading data from external sources, saving program output, and creating persistent storage for applications.

## Opening and Closing Files
\`\`\`python
# Basic file opening
file = open("example.txt", "r")
content = file.read()
file.close()

# Better approach using 'with' statement (automatically closes file)
with open("example.txt", "r") as file:
    content = file.read()
    print(content)
# File is automatically closed here
\`\`\`

## File Opening Modes
- **r**: Read mode (default)
- **w**: Write mode (overwrites existing content)
- **a**: Append mode (adds to existing content)
- **x**: Exclusive creation (fails if file exists)
- **b**: Binary mode (e.g., "rb", "wb")
- **+**: Read and write mode

## Reading Files
\`\`\`python
# Reading entire file
with open("data.txt", "r") as file:
    content = file.read()
    print(content)

# Reading line by line
with open("data.txt", "r") as file:
    for line in file:
        print(line.strip())  # strip() removes newline characters

# Reading all lines into a list
with open("data.txt", "r") as file:
    lines = file.readlines()
    print(lines)

# Reading one line at a time
with open("data.txt", "r") as file:
    first_line = file.readline()
    second_line = file.readline()
\`\`\`

## Writing Files
\`\`\`python
# Writing to a file (overwrites existing content)
with open("output.txt", "w") as file:
    file.write("Hello, World!\\n")
    file.write("This is a new line.\\n")

# Writing multiple lines
lines = ["Line 1\\n", "Line 2\\n", "Line 3\\n"]
with open("output.txt", "w") as file:
    file.writelines(lines)

# Appending to a file
with open("output.txt", "a") as file:
    file.write("This line is appended.\\n")
\`\`\`

## Working with CSV Files
\`\`\`python
import csv

# Writing CSV files
data = [
    ["Name", "Age", "City"],
    ["Alice", 25, "New York"],
    ["Bob", 30, "Los Angeles"],
    ["Charlie", 35, "Chicago"]
]

with open("people.csv", "w", newline="") as file:
    writer = csv.writer(file)
    writer.writerows(data)

# Reading CSV files
with open("people.csv", "r") as file:
    reader = csv.reader(file)
    for row in reader:
        print(row)

# Using DictReader for named columns
with open("people.csv", "r") as file:
    reader = csv.DictReader(file)
    for row in reader:
        print(f"Name: {row['Name']}, Age: {row['Age']}, City: {row['City']}")
\`\`\`

## Working with JSON Files
\`\`\`python
import json

# Writing JSON data
data = {
    "users": [
        {"name": "Alice", "age": 25, "email": "alice@email.com"},
        {"name": "Bob", "age": 30, "email": "bob@email.com"}
    ],
    "total_users": 2
}

with open("users.json", "w") as file:
    json.dump(data, file, indent=2)

# Reading JSON data
with open("users.json", "r") as file:
    loaded_data = json.load(file)
    print(loaded_data["total_users"])
    for user in loaded_data["users"]:
        print(f"User: {user['name']}, Email: {user['email']}")

# Converting between JSON strings and Python objects
json_string = json.dumps(data, indent=2)
python_object = json.loads(json_string)
\`\`\`

## File and Directory Operations
\`\`\`python
import os
import shutil

# Check if file exists
if os.path.exists("myfile.txt"):
    print("File exists")

# Get file information
import os.path
file_size = os.path.getsize("myfile.txt")
modification_time = os.path.getmtime("myfile.txt")

# Create directories
os.makedirs("my_folder/sub_folder", exist_ok=True)

# List directory contents
files = os.listdir(".")
print("Files in current directory:", files)

# Copy files
shutil.copy("source.txt", "destination.txt")

# Move/rename files
shutil.move("old_name.txt", "new_name.txt")

# Delete files
os.remove("file_to_delete.txt")

# Delete directories
shutil.rmtree("directory_to_delete")
\`\`\`

## Error Handling with Files
\`\`\`python
def safe_file_read(filename):
    try:
        with open(filename, "r") as file:
            return file.read()
    except FileNotFoundError:
        print(f"Error: File '{filename}' not found")
        return None
    except PermissionError:
        print(f"Error: Permission denied to read '{filename}'")
        return None
    except Exception as e:
        print(f"Unexpected error: {e}")
        return None

# Usage
content = safe_file_read("example.txt")
if content:
    print("File content:", content)
\`\`\`

## Working with Binary Files
\`\`\`python
# Reading binary files (e.g., images)
with open("image.jpg", "rb") as file:
    binary_data = file.read()
    print(f"File size: {len(binary_data)} bytes")

# Copying binary files
with open("source_image.jpg", "rb") as source:
    with open("copy_image.jpg", "wb") as destination:
        destination.write(source.read())
\`\`\`
  `,
  duration: "75 minutes",
  difficulty: "intermediate",
  order: 3,
  tags: ["python", "files", "csv", "json", "data-persistence"],
  objectives: [
    "Read and write text files in Python",
    "Work with CSV files for structured data",
    "Handle JSON files for data interchange",
    "Perform file and directory operations",
    "Implement proper error handling for file operations",
    "Work with binary files"
  ],
  pythonPractice: {
    task: "Build a Contact Manager that saves and loads contact data from files",
    initialCode: `import json
import csv
import os

class ContactManager:
    def __init__(self, filename="contacts.json"):
        self.filename = filename
        self.contacts = []
        self.load_contacts()
    
    def load_contacts(self):
        # Load contacts from JSON file if it exists
        pass
    
    def save_contacts(self):
        # Save contacts to JSON file
        pass
    
    def add_contact(self, name, phone, email):
        # Add a new contact
        pass
    
    def find_contact(self, name):
        # Find a contact by name
        pass
    
    def update_contact(self, name, phone=None, email=None):
        # Update an existing contact
        pass
    
    def delete_contact(self, name):
        # Delete a contact
        pass
    
    def export_to_csv(self, csv_filename="contacts.csv"):
        # Export all contacts to CSV file
        pass
    
    def import_from_csv(self, csv_filename="contacts.csv"):
        # Import contacts from CSV file
        pass
    
    def list_contacts(self):
        # Display all contacts
        pass

# Test the contact manager
# manager = ContactManager()
# manager.add_contact("Alice", "555-0123", "alice@email.com")
# manager.add_contact("Bob", "555-0456", "bob@email.com")
# manager.list_contacts()
# manager.export_to_csv()
# print("Contacts exported to CSV")
`,
    expectedCode: `import json
import csv
import os

class ContactManager:
    def __init__(self, filename="contacts.json"):
        self.filename = filename
        self.contacts = []
        self.load_contacts()
    
    def load_contacts(self):
        # Load contacts from JSON file if it exists
        if os.path.exists(self.filename):
            try:
                with open(self.filename, "r") as file:
                    self.contacts = json.load(file)
                print(f"Loaded {len(self.contacts)} contacts from {self.filename}")
            except Exception as e:
                print(f"Error loading contacts: {e}")
                self.contacts = []
        else:
            print("No existing contact file found. Starting fresh.")
    
    def save_contacts(self):
        # Save contacts to JSON file
        try:
            with open(self.filename, "w") as file:
                json.dump(self.contacts, file, indent=2)
            print(f"Contacts saved to {self.filename}")
        except Exception as e:
            print(f"Error saving contacts: {e}")
    
    def add_contact(self, name, phone, email):
        # Add a new contact
        contact = {
            "name": name,
            "phone": phone,
            "email": email
        }
        self.contacts.append(contact)
        self.save_contacts()
        print(f"Added contact: {name}")
    
    def find_contact(self, name):
        # Find a contact by name
        for contact in self.contacts:
            if contact["name"].lower() == name.lower():
                return contact
        return None
    
    def update_contact(self, name, phone=None, email=None):
        # Update an existing contact
        contact = self.find_contact(name)
        if contact:
            if phone:
                contact["phone"] = phone
            if email:
                contact["email"] = email
            self.save_contacts()
            print(f"Updated contact: {name}")
            return True
        else:
            print(f"Contact '{name}' not found")
            return False
    
    def delete_contact(self, name):
        # Delete a contact
        contact = self.find_contact(name)
        if contact:
            self.contacts.remove(contact)
            self.save_contacts()
            print(f"Deleted contact: {name}")
            return True
        else:
            print(f"Contact '{name}' not found")
            return False
    
    def export_to_csv(self, csv_filename="contacts.csv"):
        # Export all contacts to CSV file
        if not self.contacts:
            print("No contacts to export")
            return
        
        try:
            with open(csv_filename, "w", newline="") as file:
                fieldnames = ["name", "phone", "email"]
                writer = csv.DictWriter(file, fieldnames=fieldnames)
                writer.writeheader()
                writer.writerows(self.contacts)
            print(f"Exported {len(self.contacts)} contacts to {csv_filename}")
        except Exception as e:
            print(f"Error exporting to CSV: {e}")
    
    def import_from_csv(self, csv_filename="contacts.csv"):
        # Import contacts from CSV file
        if not os.path.exists(csv_filename):
            print(f"CSV file '{csv_filename}' not found")
            return
        
        try:
            with open(csv_filename, "r") as file:
                reader = csv.DictReader(file)
                imported_count = 0
                for row in reader:
                    # Check if contact already exists
                    if not self.find_contact(row["name"]):
                        self.contacts.append(row)
                        imported_count += 1
            
            if imported_count > 0:
                self.save_contacts()
                print(f"Imported {imported_count} new contacts from {csv_filename}")
            else:
                print("No new contacts to import")
        except Exception as e:
            print(f"Error importing from CSV: {e}")
    
    def list_contacts(self):
        # Display all contacts
        if not self.contacts:
            print("No contacts found")
            return
        
        print(f"\\n--- Contact List ({len(self.contacts)} contacts) ---")
        for i, contact in enumerate(self.contacts, 1):
            print(f"{i}. {contact['name']}")
            print(f"   Phone: {contact['phone']}")
            print(f"   Email: {contact['email']}")
            print()

# Test the contact manager
manager = ContactManager()
manager.add_contact("Alice", "555-0123", "alice@email.com")
manager.add_contact("Bob", "555-0456", "bob@email.com")
manager.list_contacts()
manager.export_to_csv()

# Test updating and deleting
manager.update_contact("Alice", phone="555-9999")
manager.delete_contact("Bob")
manager.list_contacts()
`,
    expectedOutput: {
      type: 'console',
      content: [
        "No existing contact file found. Starting fresh.",
        "Added contact: Alice",
        "Contacts saved to contacts.json",
        "Added contact: Bob", 
        "Contacts saved to contacts.json",
        "--- Contact List (2 contacts) ---",
        "1. Alice",
        "   Phone: 555-0123",
        "   Email: alice@email.com",
        "2. Bob",
        "   Phone: 555-0456", 
        "   Email: bob@email.com",
        "Exported 2 contacts to contacts.csv",
        "Updated contact: Alice",
        "Contacts saved to contacts.json",
        "Deleted contact: Bob",
        "Contacts saved to contacts.json"
      ]
    },
    hints: [
      "Use json.load() and json.dump() for JSON operations",
      "Check if files exist with os.path.exists()",
      "Use try-except blocks for error handling",
      "Use csv.DictReader and csv.DictWriter for CSV operations"
    ]
  },
  quiz: {
    questions: [
      {
        id: "py-file-1",
        question: "Which statement automatically closes a file when done?",
        options: ["open()", "with open() as file:", "file.close()", "close()"],
        correctAnswer: 1
      },
      {
        id: "py-file-2",
        question: "What file mode should you use to add content to the end of an existing file?",
        options: ["r", "w", "a", "x"],
        correctAnswer: 2
      },
      {
        id: "py-file-3",
        question: "Which module is used to work with JSON data in Python?",
        options: ["csv", "json", "file", "data"],
        correctAnswer: 1
      }
    ]
  }
};

// Module 4: API Integration
export const PYTHON_API_INTEGRATION_MODULE: PythonModule = {
  id: "python-intermediate-4",
  title: "API Integration and Web Requests",
  description: "Learn to work with web APIs, make HTTP requests, and integrate external services into your Python applications.",
  content: `
# API Integration and Web Requests

## Introduction to APIs
APIs (Application Programming Interfaces) allow different software applications to communicate with each other. Web APIs use HTTP protocols to exchange data.

## The requests Library
Python's requests library makes it easy to work with HTTP requests.

### Installing requests
\`\`\`bash
pip install requests
\`\`\`

## Basic HTTP Requests
\`\`\`python
import requests

# GET request
response = requests.get("https://api.github.com/users/octocat")
print(f"Status Code: {response.status_code}")
print(f"Content: {response.text}");

# Check if request was successful
if response.status_code == 200:
    print("Request successful!")
    data = response.json()  # Parse JSON response
    print(f"User: {data['name']}")
    print(f"Public Repos: {data['public_repos']}")
else:
    print(f"Request failed with status code: {response.status_code}")
\`\`\`

## Working with JSON APIs
\`\`\`python
import requests
import json

def get_weather_data(city):
    # Example using a weather API (you'd need an API key)
    api_key = "your_api_key_here"
    url = f"http://api.openweathermap.org/data/2.5/weather"
    params = {
        "q": city,
        "appid": api_key,
        "units": "metric"
    }
    
    try:
        response = requests.get(url, params=params)
        response.raise_for_status()  # Raises exception for bad status codes
        
        data = response.json()
        return {
            "city": data["name"],
            "temperature": data["main"]["temp"],
            "description": data["weather"][0]["description"],
            "humidity": data["main"]["humidity"]
        }
    except requests.exceptions.RequestException as e:
        print(f"Error fetching weather data: {e}")
        return None

# Using the function
weather = get_weather_data("London")
if weather:
    print(f"Weather in {weather['city']}: {weather['temperature']}°C")
    print(f"Description: {weather['description']}")
\`\`\`

## POST Requests and Sending Data
\`\`\`python
import requests

# Sending JSON data
def create_user(name, email):
    url = "https://jsonplaceholder.typicode.com/users"
    user_data = {
        "name": name,
        "email": email,
        "username": name.lower().replace(" ", "")
    }
    
    response = requests.post(url, json=user_data)
    
    if response.status_code == 201:
        created_user = response.json()
        print(f"User created with ID: {created_user['id']}")
        return created_user
    else:
        print(f"Failed to create user: {response.status_code}")
        return None

# Sending form data
def submit_form():
    url = "https://httpbin.org/post"
    form_data = {
        "username": "alice",
        "password": "secret123",
        "email": "alice@example.com"
    }
    
    response = requests.post(url, data=form_data)
    return response.json()

# Using the functions
new_user = create_user("Alice Smith", "alice@example.com")
form_response = submit_form()
\`\`\`

## Authentication
\`\`\`python
import requests
from requests.auth import HTTPBasicAuth

# Basic Authentication
response = requests.get(
    "https://api.example.com/protected",
    auth=HTTPBasicAuth("username", "password")
)

# API Key Authentication (common pattern)
def api_request_with_key(endpoint, api_key):
    headers = {
        "Authorization": f"Bearer {api_key}",
        "Content-Type": "application/json"
    }
    
    response = requests.get(endpoint, headers=headers)
    return response

# Token-based authentication
def authenticate_and_get_token(username, password):
    auth_url = "https://api.example.com/auth/login"
    credentials = {
        "username": username,
        "password": password
    }
    
    response = requests.post(auth_url, json=credentials)
    if response.status_code == 200:
        return response.json()["access_token"]
    return None

def make_authenticated_request(endpoint, token):
    headers = {"Authorization": f"Bearer {token}"}
    return requests.get(endpoint, headers=headers)
\`\`\`

## Error Handling and Timeouts
\`\`\`python
import requests
from requests.exceptions import RequestException, Timeout, ConnectionError

def robust_api_call(url, max_retries=3, timeout=10):
    for attempt in range(max_retries):
        try:
            response = requests.get(url, timeout=timeout)
            response.raise_for_status()  # Raises HTTPError for bad responses
            return response.json()
            
        except Timeout:
            print(f"Timeout on attempt {attempt + 1}")
        except ConnectionError:
            print(f"Connection error on attempt {attempt + 1}")
        except requests.exceptions.HTTPError as e:
            print(f"HTTP error: {e}")
            break  # Don't retry on HTTP errors
        except RequestException as e:
            print(f"Request failed: {e}")
        
        if attempt < max_retries - 1:
            print(f"Retrying in 2 seconds...")
            import time
            time.sleep(2)
    
    return None

# Usage
data = robust_api_call("https://api.github.com/users/octocat")
if data:
    print(f"Successfully retrieved data for {data['name']}")
\`\`\`

## Working with Rate Limits
\`\`\`python
import requests
import time

class APIClient:
    def __init__(self, base_url, rate_limit_delay=1):
        self.base_url = base_url
        self.rate_limit_delay = rate_limit_delay
        self.last_request_time = 0
    
    def _respect_rate_limit(self):
        """Ensure we don't exceed rate limits"""
        current_time = time.time()
        time_since_last = current_time - self.last_request_time
        
        if time_since_last < self.rate_limit_delay:
            sleep_time = self.rate_limit_delay - time_since_last
            time.sleep(sleep_time)
        
        self.last_request_time = time.time()
    
    def get(self, endpoint):
        self._respect_rate_limit()
        url = f"{self.base_url}/{endpoint}"
        response = requests.get(url)
        return response
    
    def get_multiple_users(self, usernames):
        """Get data for multiple users, respecting rate limits"""
        users_data = []
        
        for username in usernames:
            try:
                response = self.get(f"users/{username}")
                if response.status_code == 200:
                    users_data.append(response.json())
                else:
                    print(f"Failed to get data for {username}")
            except Exception as e:
                print(f"Error fetching {username}: {e}")
        
        return users_data

# Usage
github_client = APIClient("https://api.github.com", rate_limit_delay=1)
users = github_client.get_multiple_users(["octocat", "defunkt", "pjhyett"])
\`\`\`

## Creating a Simple API Wrapper
\`\`\`python
import requests

class NewsAPI:
    def __init__(self, api_key):
        self.api_key = api_key
        self.base_url = "https://newsapi.org/v2"
        self.headers = {"X-API-Key": api_key}
    
    def get_top_headlines(self, country="us", category=None):
        """Get top headlines for a country"""
        endpoint = f"{self.base_url}/top-headlines"
        params = {"country": country}
        
        if category:
            params["category"] = category
        
        response = requests.get(endpoint, headers=self.headers, params=params)
        
        if response.status_code == 200:
            return response.json()["articles"]
        else:
            print(f"Error: {response.status_code}")
            return []
    
    def search_news(self, query, language="en", sort_by="publishedAt"):
        """Search for news articles"""
        endpoint = f"{self.base_url}/everything"
        params = {
            "q": query,
            "language": language,
            "sortBy": sort_by
        }
        
        response = requests.get(endpoint, headers=self.headers, params=params)
        
        if response.status_code == 200:
            return response.json()["articles"]
        else:
            print(f"Error: {response.status_code}")
            return []

# Usage
# news_client = NewsAPI("your_api_key_here")
# headlines = news_client.get_top_headlines(country="us", category="technology")
# for article in headlines[:5]:  # Show first 5 articles
#     print(f"Title: {article['title']}")
#     print(f"Source: {article['source']['name']}")
#     print(f"URL: {article['url']}")
#     print("---")
\`\`\`
  `,
  duration: "85 minutes",
  difficulty: "intermediate",
  order: 4,
  tags: ["python", "api", "requests", "http", "json", "web-services"],
  objectives: [
    "Make HTTP GET and POST requests using Python",
    "Work with JSON APIs and parse responses",
    "Handle authentication in API requests",
    "Implement proper error handling for API calls",
    "Respect rate limits and implement retry logic",
    "Create reusable API wrapper classes"
  ],
  pythonPractice: {
    task: "Build a GitHub Repository Analyzer that fetches and analyzes repository data",
    initialCode: `import requests
import json
from datetime import datetime

class GitHubAnalyzer:
    def __init__(self):
        self.base_url = "https://api.github.com"
        self.session = requests.Session()
    
    def get_user_info(self, username):
        # Fetch basic user information
        pass
    
    def get_user_repos(self, username):
        # Fetch all repositories for a user
        pass
    
    def analyze_repo(self, owner, repo_name):
        # Get detailed information about a specific repository
        pass
    
    def get_repo_languages(self, owner, repo_name):
        # Get programming languages used in a repository
        pass
    
    def get_user_activity_summary(self, username):
        # Analyze user's GitHub activity
        pass
    
    def compare_users(self, username1, username2):
        # Compare two GitHub users
        pass

# Test the analyzer
# analyzer = GitHubAnalyzer()
# user_info = analyzer.get_user_info("octocat")
# if user_info:
#     print(f"User: {user_info['name']}")
#     print(f"Public Repos: {user_info['public_repos']}")
#     print(f"Followers: {user_info['followers']}")
`,
    expectedCode: `import requests
import json
from datetime import datetime

class GitHubAnalyzer:
    def __init__(self):
        self.base_url = "https://api.github.com"
        self.session = requests.Session()
        # Set a user agent to be polite to the API
        self.session.headers.update({
            "User-Agent": "GitHub-Analyzer-Python/1.0"
        })
    
    def get_user_info(self, username):
        # Fetch basic user information
        try:
            url = f"{self.base_url}/users/{username}"
            response = self.session.get(url)
            response.raise_for_status()
            return response.json()
        except requests.exceptions.RequestException as e:
            print(f"Error fetching user info for {username}: {e}")
            return None
    
    def get_user_repos(self, username):
        # Fetch all repositories for a user
        try:
            url = f"{self.base_url}/users/{username}/repos"
            params = {"per_page": 100, "sort": "updated"}
            response = self.session.get(url, params=params)
            response.raise_for_status()
            return response.json()
        except requests.exceptions.RequestException as e:
            print(f"Error fetching repos for {username}: {e}")
            return []
    
    def analyze_repo(self, owner, repo_name):
        # Get detailed information about a specific repository
        try:
            url = f"{self.base_url}/repos/{owner}/{repo_name}"
            response = self.session.get(url)
            response.raise_for_status()
            repo_data = response.json()
            
            # Get additional statistics
            languages = self.get_repo_languages(owner, repo_name)
            
            analysis = {
                "name": repo_data["name"],
                "description": repo_data["description"],
                "stars": repo_data["stargazers_count"],
                "forks": repo_data["forks_count"],
                "language": repo_data["language"],
                "languages": languages,
                "size": repo_data["size"],
                "created_at": repo_data["created_at"],
                "updated_at": repo_data["updated_at"],
                "open_issues": repo_data["open_issues_count"]
            }
            
            return analysis
        except requests.exceptions.RequestException as e:
            print(f"Error analyzing repo {owner}/{repo_name}: {e}")
            return None
    
    def get_repo_languages(self, owner, repo_name):
        # Get programming languages used in a repository
        try:
            url = f"{self.base_url}/repos/{owner}/{repo_name}/languages"
            response = self.session.get(url)
            response.raise_for_status()
            return response.json()
        except requests.exceptions.RequestException as e:
            print(f"Error fetching languages for {owner}/{repo_name}: {e}")
            return {}
    
    def get_user_activity_summary(self, username):
        # Analyze user's GitHub activity
        user_info = self.get_user_info(username)
        repos = self.get_user_repos(username)
        
        if not user_info or not repos:
            return None
        
        # Calculate statistics
        total_stars = sum(repo["stargazers_count"] for repo in repos)
        total_forks = sum(repo["forks_count"] for repo in repos)
        languages = {}
        
        # Count languages across all repos
        for repo in repos:
            if repo["language"]:
                languages[repo["language"]] = languages.get(repo["language"], 0) + 1
        
        # Find most popular repo
        most_popular = max(repos, key=lambda x: x["stargazers_count"]) if repos else None
        
        summary = {
            "username": username,
            "name": user_info["name"],
            "public_repos": user_info["public_repos"],
            "followers": user_info["followers"],
            "following": user_info["following"],
            "total_stars": total_stars,
            "total_forks": total_forks,
            "most_used_languages": sorted(languages.items(), key=lambda x: x[1], reverse=True)[:5],
            "most_popular_repo": {
                "name": most_popular["name"],
                "stars": most_popular["stargazers_count"],
                "description": most_popular["description"]
            } if most_popular else None
        }
        
        return summary
    
    def compare_users(self, username1, username2):
        # Compare two GitHub users
        summary1 = self.get_user_activity_summary(username1)
        summary2 = self.get_user_activity_summary(username2)
        
        if not summary1 or not summary2:
            return None
        
        comparison = {
            "user1": summary1,
            "user2": summary2,
            "comparison": {
                "more_repos": username1 if summary1["public_repos"] > summary2["public_repos"] else username2,
                "more_followers": username1 if summary1["followers"] > summary2["followers"] else username2,
                "more_stars": username1 if summary1["total_stars"] > summary2["total_stars"] else username2
            }
        }
        
        return comparison

# Test the analyzer
analyzer = GitHubAnalyzer()

# Test user info
print("=== User Info ===")
user_info = analyzer.get_user_info("octocat")
if user_info:
    print(f"User: {user_info['name']}")
    print(f"Public Repos: {user_info['public_repos']}")
    print(f"Followers: {user_info['followers']}")

# Test user activity summary
print("\\n=== Activity Summary ===")
activity = analyzer.get_user_activity_summary("octocat")
if activity:
    print(f"Total Stars: {activity['total_stars']}")
    print(f"Total Forks: {activity['total_forks']}")
    print(f"Most Used Languages: {activity['most_used_languages'][:3]}")

# Test repo analysis
print("\\n=== Repository Analysis ===")
repo_analysis = analyzer.analyze_repo("octocat", "Hello-World")
if repo_analysis:
    print(f"Repo: {repo_analysis['name']}")
    print(f"Stars: {repo_analysis['stars']}")
    print(f"Primary Language: {repo_analysis['language']}")
    print(f"Size: {repo_analysis['size']} KB")
`,
    expectedOutput: {
      type: 'console',
      content: [
        "=== User Info ===",
        "User: The Octocat",
        "Public Repos: 8",
        "Followers: 9347",
        "=== Activity Summary ===",
        "Total Stars: 2107",
        "Total Forks: 1982", 
        "Most Used Languages: [('C', 2), ('Assembly', 1), ('HTML', 1)]",
        "=== Repository Analysis ===",
        "Repo: Hello-World",
        "Stars: 2107",
        "Primary Language: C",
        "Size: 108 KB"
      ]
    },
    hints: [
      "Use requests.Session() for better performance with multiple requests",
      "Always handle exceptions with try-except blocks",
      "Use response.raise_for_status() to check for HTTP errors",
      "Set appropriate headers like User-Agent for API requests"
    ]
  },
  quiz: {
    questions: [
      {
        id: "py-api-1",
        question: "Which HTTP method is typically used to retrieve data from an API?",
        options: ["POST", "GET", "PUT", "DELETE"],
        correctAnswer: 1
      },
      {
        id: "py-api-2", 
        question: "What does response.raise_for_status() do?",
        options: ["Returns the status code", "Raises an exception for HTTP errors", "Checks if response is JSON", "Validates the response"],
        correctAnswer: 1
      },
      {
        id: "py-api-3",
        question: "Which parameter in requests.get() is used to set a timeout?",
        options: ["delay", "timeout", "wait", "timer"],
        correctAnswer: 1
      }
    ]
  }
};
